import { APIProvider } from "@vis.gl/react-google-maps"
import React, { useCallback, useState } from "react"

import { Analytics } from "@bounce/analytics"
import type { Nullable } from "@bounce/util"
import { useIsMobile } from "@bounce/web-components"

import MapContainer from "@/components/LuggageStorage/Map/Interactive/MapContainer"
import { MemoizedStoreMarker } from "@/components/LuggageStorage/Map/Interactive/StoreMarker/StoreMarker"
import { config } from "@/config"
import { useSectionViewed } from "@/hooks/useSectionViewed"
import { InteractionEvents } from "@/libs/analytics/events"
import type { CityData } from "@/libs/bounce/cities"
import type { NearbyStorageSpot } from "@/libs/bounce/stores/types"

export type InteractiveMapProps = {
  city: CityData
  getStoreUrl: (store: NearbyStorageSpot) => string
  nearestStores: NearbyStorageSpot[]
}

const MOBILE_MAP_LATITUDE_OFFSET = 0.001
const MAX_STORE_MARKER = 50

const InteractiveMap = ({ city, getStoreUrl, nearestStores }: InteractiveMapProps) => {
  const { sectionRef } = useSectionViewed({ name: "InteractiveCityMap" })
  const [selectedStoreId, setSelectedStoreId] = useState<Nullable<string>>(null)

  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number } | undefined>(undefined)
  const isMobile = useIsMobile()

  const handleMarkerClick = useCallback(
    (store: NearbyStorageSpot) => {
      setSelectedStoreId(store.id)

      // Calculate offset to provide space for tooltip, more on mobile
      const latOffset = isMobile ? MOBILE_MAP_LATITUDE_OFFSET : 0

      setMapCenter({
        lat: store.coordinates.latitude + latOffset,
        lng: store.coordinates.longitude,
      })

      Analytics.track(InteractionEvents.MapLocationPressed)
    },
    [isMobile],
  )

  const onDismiss = useCallback(() => {
    setSelectedStoreId(null)
  }, [])

  return (
    <section ref={sectionRef}>
      <div className="h-[218px] md:h-[428px]">
        <APIProvider apiKey={config.googleMaps.key}>
          <MapContainer
            center={mapCenter}
            initialRegion={{
              latitude: city.coordinates.latitude,
              longitude: city.coordinates.longitude,
            }}
            onDragStart={() => setMapCenter(undefined)}
            onClick={() => {
              setMapCenter(undefined)
            }}>
            {nearestStores.slice(0, MAX_STORE_MARKER).map(store => (
              <MemoizedStoreMarker
                key={store.id}
                store={store}
                isSelected={selectedStoreId === store.id}
                onClick={handleMarkerClick}
                onDismiss={onDismiss}
                href={getStoreUrl(store)}
              />
            ))}
          </MapContainer>
        </APIProvider>
      </div>
    </section>
  )
}

export default InteractiveMap
